import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageCircle, Phone, Mail } from 'lucide-react';
import type { HealthTier, MemberHealth } from '@techsofit/api-types';
import { Card, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { getMemberHealth } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { waDigits } from '../lib/format';

const TIERS: Record<HealthTier, { label: string; hint: string; dot: string; pill: string; card: string; ring: string }> = {
  LIKELY_TO_CANCEL: {
    label: 'Likely to cancel',
    hint: 'Lapsed, or 3+ weeks without a check-in',
    dot: 'bg-bad',
    pill: 'bg-bad-soft text-bad-ink',
    card: 'bg-bad-soft',
    ring: 'ring-bad',
  },
  AT_RISK: {
    label: 'At risk',
    hint: 'Expiring or unpaid, or 10+ days quiet',
    dot: 'bg-warn',
    pill: 'bg-warn-soft text-warn-ink',
    card: 'bg-warn-soft',
    ring: 'ring-warn',
  },
  HEALTHY: {
    label: 'Healthy',
    hint: 'Active and checking in regularly',
    dot: 'bg-ok',
    pill: 'bg-ok-soft text-ok-ink',
    card: 'bg-ok-soft',
    ring: 'ring-ok',
  },
};

/** Retention triage: every active member scored 🟢/🟡/🔴 so staff can nudge before a cancellation. */
export function MemberHealthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, loading, error } = useAsync(getMemberHealth);
  // Lead with the members who need attention; clicking a summary card filters to that tier.
  // Deep-link: the dashboard insights link here with ?tier=LIKELY_TO_CANCEL etc.
  const [tier, setTier] = useState<HealthTier | 'ALL'>(() => {
    const t = searchParams.get('tier');
    return t && t in TIERS ? (t as HealthTier) : 'ALL';
  });

  const members = useMemo(() => {
    const all = data?.members ?? [];
    return tier === 'ALL' ? all : all.filter((m) => m.tier === tier);
  }, [data, tier]);

  function nudge(m: MemberHealth) {
    const num = waDigits(m.whatsappPhone ?? m.phone);
    if (num) window.open(`https://wa.me/${num}`, '_blank', 'noopener');
  }
  function call(m: MemberHealth) {
    if (m.phone) window.open(`tel:${m.phone}`);
  }
  function emailMember(m: MemberHealth) {
    if (m.email) window.open(`mailto:${m.email}`);
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="Retention"
        title="Member health"
        description="Every active member is scored on their membership, check-in recency and how often they visit, so you can reach out before they drift away."
      />

      {loading ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="mt-3 h-80" />
        </>
      ) : error || !data ? (
        <EmptyState title="Couldn’t load member health" description={error ?? 'Please refresh.'} />
      ) : (
        <>
          {/* Summary — each card filters the list to its tier */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <TierCard tier="LIKELY_TO_CANCEL" count={data.likelyToCancel} active={tier} onSelect={setTier} />
            <TierCard tier="AT_RISK" count={data.atRisk} active={tier} onSelect={setTier} />
            <TierCard tier="HEALTHY" count={data.healthy} active={tier} onSelect={setTier} />
          </div>

          <details className="mt-3 rounded-card bg-card p-4 text-sm shadow-card">
            <summary className="cursor-pointer font-medium text-ink">How we score members</summary>
            <div className="mt-3 flex flex-col gap-3 text-ink-soft">
              <p>
                A member's tier is the more serious of two checks, their membership and their attendance. The
                thresholds are set for monthly memberships.
              </p>
              <ul className="flex flex-col gap-2">
                <li className="flex gap-2">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-bad" aria-hidden />
                  <span>
                    <span className="font-medium text-ink">Likely to cancel.</span> Membership has lapsed, or they are
                    paying but have not checked in for 3 weeks or more (or not at all in 45 days).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-warn" aria-hidden />
                  <span>
                    <span className="font-medium text-ink">At risk.</span> Membership is expiring soon or unpaid, or they
                    have not checked in for 10 to 20 days, or came just once in the last 30 days.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-ok" aria-hidden />
                  <span>
                    <span className="font-medium text-ink">Healthy.</span> Active membership and checking in regularly,
                    seen in the last 10 days with two or more visits this month.
                  </span>
                </li>
              </ul>
              <p className="text-muted">
                New members get a two week grace period before attendance counts. Each member below shows the exact
                reasons behind their tier.
              </p>
            </div>
          </details>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted">
              {tier === 'ALL'
                ? `${data.total} member${data.total === 1 ? '' : 's'}`
                : `${members.length} ${TIERS[tier].label.toLowerCase()}`}
            </p>
            {tier !== 'ALL' && (
              <button type="button" onClick={() => setTier('ALL')} className="text-sm text-muted hover:text-ink">
                Show all
              </button>
            )}
          </div>

          <Card className="mt-2 overflow-hidden p-0">
            {members.length ? (
              <ul className="divide-y divide-line">
                {members.map((m) => (
                  <li key={m.memberId} className="flex items-center gap-3 px-4 py-3 md:px-5">
                    <button
                      type="button"
                      onClick={() => navigate(`/members/${m.memberId}`)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <span className={cn('size-2.5 shrink-0 rounded-full', TIERS[m.tier].dot)} aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-ink">{m.fullName}</p>
                        <p className="truncate text-xs text-muted">
                          {m.reasons.join(' · ')}
                          {m.currentPlanName && ` · ${m.currentPlanName}`}
                        </p>
                      </div>
                      <span className={cn('hidden shrink-0 rounded-pill px-2.5 py-0.5 text-xs font-medium sm:inline', TIERS[m.tier].pill)}>
                        {TIERS[m.tier].label}
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-0.5">
                      {(m.whatsappPhone || m.phone) && (
                        <button
                          type="button"
                          onClick={() => nudge(m)}
                          aria-label={`WhatsApp ${m.fullName}`}
                          title={`WhatsApp ${m.fullName}`}
                          className="inline-flex size-9 items-center justify-center rounded-control text-muted transition-colors hover:bg-ok-soft hover:text-ok-ink"
                        >
                          <MessageCircle className="size-4" aria-hidden />
                        </button>
                      )}
                      {m.phone && (
                        <button
                          type="button"
                          onClick={() => call(m)}
                          aria-label={`Call ${m.fullName}`}
                          title={`Call ${m.fullName}`}
                          className="inline-flex size-9 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface hover:text-ink"
                        >
                          <Phone className="size-4" aria-hidden />
                        </button>
                      )}
                      {m.email && (
                        <button
                          type="button"
                          onClick={() => emailMember(m)}
                          aria-label={`Email ${m.fullName}`}
                          title={`Email ${m.fullName}`}
                          className="inline-flex size-9 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface hover:text-ink"
                        >
                          <Mail className="size-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title={tier === 'HEALTHY' ? 'No healthy members in view' : 'Nobody here'}
                description={
                  tier === 'LIKELY_TO_CANCEL'
                    ? 'No one is flagged as likely to cancel. Nice.'
                    : 'No members match this filter.'
                }
                className="bg-surface"
              />
            )}
          </Card>
        </>
      )}
    </section>
  );
}

function TierCard({
  tier,
  count,
  active,
  onSelect,
}: {
  tier: HealthTier;
  count: number;
  active: HealthTier | 'ALL';
  onSelect: (t: HealthTier | 'ALL') => void;
}) {
  const t = TIERS[tier];
  const selected = active === tier;
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? 'ALL' : tier)}
      aria-pressed={selected}
      className={cn(
        'flex flex-col rounded-card p-5 text-left shadow-card transition',
        t.card,
        selected ? cn('ring-2', t.ring) : 'hover:-translate-y-0.5 hover:shadow-card-hover',
      )}
    >
      <p className="font-display text-metric tabular text-ink">{count}</p>
      <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-ink">
        <span className={cn('size-2 rounded-full', t.dot)} aria-hidden />
        {t.label}
      </p>
      <p className="mt-1.5 text-xs text-ink/60">{t.hint}</p>
    </button>
  );
}
